// Portfolio map (/office/map, Session AG Item 4). A full-page Leaflet map of every site, each a
// coloured marker by its worst lift service status (red overdue / amber due soon / green current /
// grey no contract). Leaflet + OpenStreetMap tiles + Nominatim geocoding (the locked mapping
// decision): zero cost, no API key. Geocoding runs server-side; this page reads GET /api/map/sites
// (which kicks off geocoding for any un-located site), draws the markers, and lists the sites it
// could not place yet. Leaflet is driven directly via useEffect (no React wrapper dependency).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../lib/api";
import type { MapSite, MapMarkerStatus } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";

// Marker fills: the locked functional status colours (brand.md: red #c0392b, amber #b8860b, green
// #63C41F, a neutral grey), the same four tones the lift compliance pill uses. They are hex values
// because Leaflet paints an SVG fill, which cannot read a CSS custom property. (The map used the
// off-palette #ef4444 / #f59e0b / #6b7280 until design system Session 5.)
const MARKER_FILL: Record<MapMarkerStatus, string> = {
  red: "#c0392b",
  amber: "#b8860b",
  green: "#63C41F",
  grey: "#9ca3af",
};
const STATUS_LABEL: Record<MapMarkerStatus, string> = {
  red: "Overdue for service",
  amber: "Due within 90 days",
  green: "Current",
  grey: "No active contract",
};

// East London (Walthamstow, near the ELE trading base) is the default view, and it STAYS the view:
// the map opens here and is never auto-recentred on the markers (a single test/migrated site would
// otherwise drag the whole view to it). The office can re-centre on demand with "Fit to markers".
// Exported so a unit test can pin the coordinates.
export const DEFAULT_CENTER: [number, number] = [51.5886, -0.0118];
export const DEFAULT_ZOOM = 10;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

// Combine a site's address with its postcode for display, never repeating the postcode when the
// address already ends with it. Many migrated addresses already include the postcode, which
// produced a doubled "11 The Avenue, Radlett, WD7 7DG, WD7 7DG". Case-insensitive and tolerant of a
// trailing comma/space on the address. Exported for unit testing.
export function formatSiteAddress(address: string, postcode: string | null | undefined): string {
  const a = (address ?? "").trim().replace(/[,\s]+$/, "");
  const p = (postcode ?? "").trim();
  if (!p) return a;
  if (!a) return p;
  if (a.toLowerCase().endsWith(p.toLowerCase())) return a;
  return `${a}, ${p}`;
}

export function PortfolioMapPage() {
  const navigate = useNavigate();
  const [sites, setSites] = useState<MapSite[] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [refreshKey, setRefreshKey] = useState(0);
  const [geocoding, setGeocoding] = useState(false);
  const [geoMsg, setGeoMsg] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    let active = true;
    setState("loading");
    void (async () => {
      try {
        const s = await api.map.sites();
        if (active) {
          setSites(s);
          setState("ready");
        }
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const located = useMemo(() => (sites ?? []).filter((s) => s.lat != null && s.lng != null), [sites]);
  const unlocated = useMemo(() => (sites ?? []).filter((s) => s.lat == null || s.lng == null), [sites]);

  // Initialise the Leaflet map once the container is in the DOM. Guarded so a non-layout environment
  // (e.g. the test renderer) cannot crash the page.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    try {
      const map = L.map(containerRef.current, { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, scrollWheelZoom: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
    } catch {
      // Leaflet could not initialise (no real layout); the markers list + "could not map" still render.
    }
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Draw / redraw the markers whenever the located sites change. The view is NEVER recentred
  // here (no fitBounds on load or refresh), so the East London default holds and the office's
  // current pan/zoom survives a Refresh; re-centring is an explicit "Fit to markers" action.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    try {
      layer.clearLayers();
      for (const s of located) {
        const lat = s.lat!;
        const lng = s.lng!;
        const marker = L.circleMarker([lat, lng], {
          radius: 9,
          color: "#050816",
          weight: 1,
          fillColor: MARKER_FILL[s.worstStatus],
          fillOpacity: 0.9,
        });
        const el = document.createElement("div");
        // The width goes on through the CSSOM rather than as a style attribute in the markup: the
        // Content Security Policy governs inline style attributes in markup, not element.style
        // (Session ASB; server/src/config/contentSecurityPolicy.ts).
        const details = document.createElement("div");
        details.style.minWidth = "180px";
        details.innerHTML =
          `<strong>${escapeHtml(s.name)}</strong><br/>` +
          `<span>${escapeHtml(formatSiteAddress(s.address, s.postcode))}</span><br/>` +
          `<span>${s.liftCount} ${s.liftCount === 1 ? "lift" : "lifts"} | ${STATUS_LABEL[s.worstStatus]}</span><br/>`;
        el.appendChild(details);
        const link = document.createElement("button");
        link.textContent = "View site";
        link.style.cssText = "margin-top:6px;color:#63C41F;background:none;border:none;cursor:pointer;font-size:11px;letter-spacing:.05em";
        link.onclick = () => navigate(`/office/sites/${s.id}`);
        el.appendChild(link);
        marker.bindPopup(el);
        marker.addTo(layer);
      }
    } catch {
      // Drawing failed in a non-layout environment; ignore (the lists below still render).
    }
  }, [located, navigate]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Re-centre the map on the located markers, but only when the office asks for it.
  const fitToMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || located.length === 0) return;
    try {
      const points = located.map((s) => [s.lat!, s.lng!] as [number, number]);
      map.fitBounds(L.latLngBounds(points).pad(0.2), { maxZoom: 14 });
    } catch {
      // Non-layout environment (e.g. tests); ignore.
    }
  }, [located]);

  // Geocode every un-located site in one pass (rate-limited server-side; takes ~1s per site), then
  // refresh the map so the newly located sites appear.
  const geocodeAll = useCallback(async () => {
    setGeocoding(true);
    setGeoMsg(null);
    try {
      const summary = await api.map.geocodeAll();
      if (summary.queued === 0) {
        setGeoMsg("All sites are already located.");
      } else {
        const sitesWord = summary.queued === 1 ? "site" : "sites";
        const failedNote = summary.failed > 0 ? ` ${summary.failed} could not be located from their address.` : "";
        setGeoMsg(`${summary.succeeded} of ${summary.queued} ${sitesWord} geocoded.${failedNote}`);
      }
      refresh();
    } catch (err) {
      // Surface what actually went wrong (never silently swallow it) without ever showing a raw
      // machine code (X-2, Session AQV review fix): friendlyApiError only surfaces server text that
      // reads like a sentence, the same rule every other office error message follows.
      setGeoMsg(friendlyApiError(err, "Could not geocode sites. Please try again in a moment."));
    } finally {
      setGeocoding(false);
    }
  }, [refresh]);

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Map"
        subtitle="Every site, coloured by its worst lift service status. Sites are located automatically from their address."
        actions={
          <>
            <Button variant="accent" size="sm" onClick={() => void geocodeAll()} disabled={geocoding || state === "loading"}>
              {geocoding ? "Geocoding..." : "Geocode all sites"}
            </Button>
            <button
              type="button"
              onClick={fitToMarkers}
              disabled={located.length === 0}
              className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
            >
              Fit to markers
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={state === "loading"}
              className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
            >
              {state === "loading" ? "Refreshing..." : "Refresh"}
            </button>
          </>
        }
      />

      {geocoding && (
        <p role="status" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
          Locating sites from their addresses. This can take up to a minute for a large portfolio. Please wait.
        </p>
      )}
      {geoMsg && !geocoding && (
        <p role="status" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {geoMsg}
        </p>
      )}

      {/* Legend */}
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-ele-body">
        {(["red", "amber", "green", "grey"] as const).map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: MARKER_FILL[s] }} />
            {STATUS_LABEL[s]}
          </span>
        ))}
      </div>

      {state === "error" ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the map. Please try again in a moment.
        </p>
      ) : (
        <>
          <div
            ref={containerRef}
            data-testid="portfolio-map"
            className="h-[65vh] w-full overflow-hidden rounded-ele border border-ele-purple-accent"
            style={{ background: "#0b1020" }}
          />
          {state === "loading" && !sites ? <Loader shape="inline" label="Loading sites..." className="mt-3" /> : null}

          {unlocated.length > 0 && (
            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h2 className="mb-1 font-heading text-lg text-ele-blue">Could not map ({unlocated.length})</h2>
              <p className="mb-2 text-xs text-ele-body">
                These sites do not have a location yet. They are being located in the background from their address, so
                Refresh shortly. If one never appears, check the site has a valid UK postcode.
              </p>
              <ul className="space-y-1 text-sm">
                {unlocated.map((s) => (
                  // Session ACW Loop 6: min-w-0 lets each flex item shrink below its content's
                  // natural width (the flex default of min-width:auto otherwise refuses to), and
                  // break-words wraps a long site name or address onto a second line instead of
                  // pushing the row wider than the phone viewport.
                  <li key={s.id} className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="min-w-0 break-words text-ele-strong">{s.name}</span>
                    <span className="min-w-0 break-words text-ele-body">{formatSiteAddress(s.address, s.postcode)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
